import type { HTMLProps, MouseEvent, ReactNode } from 'react'
import { forwardRef } from 'react'
import React from 'react'

import classNames from 'classnames'

import type { ButtonAppearance } from '../../types'
import { Loader } from '../Loader'
import styles from './Button.module.scss'

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      type = 'button',
      appearance = 'primary',
      disabled = false,
      className,
      enableSpinner = false,
      onClick,
      ...rest
    },
    ref
  ) => {
    const buttonClassNames = classNames(
      styles.button,
      styles[`button-${appearance}`],
      {
        [styles['button-disabled']]: disabled,
        [styles['button-spinner']]: enableSpinner,
      },
      className
    )

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e)
      }
    }

    return enableSpinner ? (
      <button
        className={buttonClassNames}
        {...(disabled && { 'aria-disabled': 'true' })}
        disabled={disabled}
        type={type}
        ref={ref}
        {...rest}
        aria-busy="true"
      >
        <Loader />
      </button>
    ) : (
      <button
        className={buttonClassNames}
        {...(disabled && { 'aria-disabled': 'true' })}
        disabled={disabled}
        onClick={handleClick}
        type={type}
        ref={ref}
        {...rest}
        aria-busy="false"
      >
        {children}
      </button>
    )
  }
)

///////// IMPLEMENTATION /////////

type Props = {
  label?: string
  appearance?: ButtonAppearance
  className?: string
  disabled?: boolean
  enableSpinner?: boolean
  children?: ReactNode
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
  type?: HTMLButtonElement['type']
} & HTMLProps<HTMLButtonElement>
